import React from 'react';
import SearchInput from './SearchInput';
import { ETAPAS } from '../utils/emendaEtapa';

interface EmendasFilterBarProps {
  query: string;
  onQueryChange: (v: string) => void;
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
  etapaFilter: string;
  onEtapaFilterChange: (v: string) => void;
  /** Oculta o seletor de etapa (quando a página oferece esse filtro em outro lugar). */
  hideEtapa?: boolean;
  /** Filtro de tipo de transferência (Direta/Indireta) — só renderiza se o handler existir. */
  transferencia?: string;
  onTransferenciaChange?: (v: string) => void;
  /** Filtro de esfera (Municipal/Estadual/Federal) — só renderiza se o handler existir. */
  esfera?: string;
  onEsferaChange?: (v: string) => void;
  /** Filtro de devolução à Câmara — só renderiza se o handler existir. */
  camara?: string;
  onCamaraChange?: (v: string) => void;
  detailFilter: string;
  onDetailFilterChange: (v: string) => void;
  year: string;
  onYearChange: (v: string) => void;
  areaFilter?: string;
  onAreaFilterChange?: (v: string) => void;
  areas?: { value: string; label: string }[];
  anos?: string[];
  vereador?: string;
  onVereadorChange?: (v: string) => void;
  vereadores?: string[];
  canFilterByVereador?: boolean;
  secretaria?: string;
  onSecretariaChange?: (v: string) => void;
  secretarias?: string[];
  canFilterBySecretaria?: boolean;
  myQueueOnly: boolean;
  onMyQueueToggle: () => void;
  myQueueCount: number;
  myQueueStatuses: string[];
  isQueueRestricted: boolean;
  onClearFilters: () => void;
  variant?: 'legacy' | 'dashboard';
}

const controlClass =
  'min-w-[140px] flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';

const EmendasFilterBar: React.FC<EmendasFilterBarProps> = ({
  query,
  onQueryChange,
  statusFilter,
  onStatusFilterChange,
  etapaFilter,
  onEtapaFilterChange,
  detailFilter,
  onDetailFilterChange,
  year,
  onYearChange,
  areaFilter = '',
  onAreaFilterChange,
  areas = [],
  anos,
  vereador = '',
  onVereadorChange,
  vereadores = [],
  canFilterByVereador = false,
  secretaria = '',
  onSecretariaChange,
  secretarias = [],
  canFilterBySecretaria = false,
  myQueueOnly,
  onMyQueueToggle,
  myQueueCount,
  myQueueStatuses,
  isQueueRestricted,
  onClearFilters,
  variant = 'legacy',
  hideEtapa = false,
  transferencia = '',
  onTransferenciaChange,
  esfera = '',
  onEsferaChange,
  camara = '',
  onCamaraChange,
}) => {
  const anoOptions = anos ?? ['2026', '2025', '2024', '2023', '2022'];

  if (variant === 'dashboard') {
    return (
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[260px] flex-[3]">
          <SearchInput
            value={query}
            onChange={onQueryChange}
            placeholder="Buscar por descricao, codigo, instituicao ou parlamentar..."
          />
        </div>

        {canFilterByVereador && onVereadorChange && (
          <select value={vereador} onChange={(e) => onVereadorChange(e.target.value)} className={controlClass}>
            <option value="">Todos os vereadores</option>
            {vereadores.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}

        {canFilterBySecretaria && onSecretariaChange && (
          <select value={secretaria} onChange={(e) => onSecretariaChange(e.target.value)} className={controlClass}>
            <option value="">Todas as secretarias</option>
            {secretarias.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {!hideEtapa && (
          <select value={etapaFilter} onChange={(e) => onEtapaFilterChange(e.target.value)} className={controlClass}>
            <option value="Todas">Todas as etapas</option>
            {ETAPAS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}

        {onTransferenciaChange && (
          <select value={transferencia} onChange={(e) => onTransferenciaChange(e.target.value)} className={controlClass}>
            <option value="">Todas as transferências</option>
            <option value="Direta">Direta</option>
            <option value="Indireta">Indireta</option>
          </select>
        )}

        {onEsferaChange && (
          <select value={esfera} onChange={(e) => onEsferaChange(e.target.value)} className={controlClass}>
            <option value="">Todas as esferas</option>
            <option value="Municipal">Municipal</option>
            <option value="Estadual">Estadual</option>
            <option value="Federal">Federal</option>
          </select>
        )}

        {onCamaraChange && (
          <select value={camara} onChange={(e) => onCamaraChange(e.target.value)} className={controlClass}>
            <option value="">Todas (Câmara)</option>
            <option value="EM_APROVACAO">Em aprovação na Câmara</option>
            <option value="AGUARDANDO_DEVOLUCAO">Aguardando devolução à Câmara</option>
            <option value="DEVOLVIDA">Devolvidas à Câmara</option>
          </select>
        )}

        {onAreaFilterChange && (
          <select value={areaFilter} onChange={(e) => onAreaFilterChange(e.target.value)} className={controlClass}>
            <option value="">Todas as areas</option>
            {areas.map((area) => <option key={area.value} value={area.value}>{area.label}</option>)}
          </select>
        )}

        <select
          value={year}
          onChange={(e) => onYearChange(e.target.value)}
          className="min-w-[120px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          <option value="">Todos os anos</option>
          {anoOptions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <div className="flex items-center gap-2">
          {isQueueRestricted ? (
            <span className="inline-flex items-center gap-2 rounded-xl border border-sky-600 bg-sky-600 px-3 py-2 text-sm font-medium text-white dark:border-sky-500">
              Minha Fila
              {myQueueCount > 0 && (
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-sky-600">
                  {myQueueCount}
                </span>
              )}
            </span>
          ) : myQueueStatuses.length > 0 ? (
            <button
              type="button"
              onClick={onMyQueueToggle}
              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                myQueueOnly
                  ? 'border-sky-600 bg-sky-600 text-white dark:border-sky-500'
                  : 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300 dark:hover:bg-sky-900/50'
              }`}
            >
              Minha Fila
              {myQueueCount > 0 && (
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                    myQueueOnly ? 'bg-white text-sky-600' : 'bg-sky-600 text-white'
                  }`}
                >
                  {myQueueCount}
                </span>
              )}
            </button>
          ) : null}

          <button
            type="button"
            onClick={onClearFilters}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Limpar filtros
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={query}
          onChange={onQueryChange}
          placeholder="Buscar por descricao, codigo, instituicao ou parlamentar..."
          className="w-80"
        />
        <select value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
          <option value="Todas">Todos os status</option>
          <option>Recebido</option>
          <option>Iniciado</option>
          <option>Em execucao</option>
          <option>Devolvido</option>
          <option>Concluido</option>
        </select>
        {!hideEtapa && (
          <select value={etapaFilter} onChange={(e) => onEtapaFilterChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
            <option value="Todas">Todas as etapas</option>
            {ETAPAS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
        <select value={detailFilter} onChange={(e) => onDetailFilterChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
          <option>Todas</option>
          <option>Com Detalhamento</option>
          <option>Sem Detalhamento</option>
        </select>
        <select value={year} onChange={(e) => onYearChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
          <option value="">Todos os anos</option>
          {anoOptions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {onTransferenciaChange && (
          <select value={transferencia} onChange={(e) => onTransferenciaChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
            <option value="">Todas as transferências</option>
            <option value="Direta">Direta</option>
            <option value="Indireta">Indireta</option>
          </select>
        )}
        {onEsferaChange && (
          <select value={esfera} onChange={(e) => onEsferaChange(e.target.value)} className="rounded border px-3 py-2 text-sm">
            <option value="">Todas as esferas</option>
            <option value="Municipal">Municipal</option>
            <option value="Estadual">Estadual</option>
            <option value="Federal">Federal</option>
          </select>
        )}
        {isQueueRestricted ? (
          <span className="inline-flex items-center gap-2 rounded border border-indigo-600 bg-indigo-600 px-3 py-2 text-sm font-medium text-white">
            Minha Fila
            {myQueueCount > 0 && (
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-indigo-600">
                {myQueueCount}
              </span>
            )}
          </span>
        ) : myQueueStatuses.length > 0 ? (
          <button
            type="button"
            onClick={onMyQueueToggle}
            className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors ${
              myQueueOnly
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50'
            }`}
          >
            Minha Fila
            {myQueueCount > 0 && (
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                  myQueueOnly ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white'
                }`}
              >
                {myQueueCount}
              </span>
            )}
          </button>
        ) : null}
      </div>
      <div className="flex gap-2">
        <button type="button" className="rounded bg-blue-600 px-4 py-2 text-sm text-white">Pesquisar</button>
        <button type="button" onClick={onClearFilters} className="rounded border px-4 py-2 text-sm">
          Limpar
        </button>
      </div>
    </div>
  );
};

export default EmendasFilterBar;
